/*
 * Creative Cycles Workshop bike builder, <ccw-builder>.
 *
 * Reads the radio inputs the section rendered, keeps the running price and weight, swaps the bike
 * image when the wheels change, mirrors the build into the URL so it can be shared, and fills the
 * review panel and the hidden form fields before the contact form posts.
 */
(() => {
  const money = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(Math.round(n)).toLocaleString('en-AU');
  const CATEGORY_LABELS = {};
  const SENT_FLAG = 'ccw-build-sent';

  class CcwBuilder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-choices]');
      this.review = this.querySelector('[data-review]');
      this.sent = this.querySelector('[data-sent]');
      this.bike = this.querySelector('.ccw-builder__bike');
      // The model photo, kept so the standard colour can bring it back after a frame photo
      this.bikePhoto = this.bike && { src: this.bike.getAttribute('src'), srcset: this.bike.getAttribute('srcset'), sizes: this.bike.getAttribute('sizes') };
      this.colourLabel = this.querySelector('[data-colour-label]');
      this.priceEl = this.querySelector('[data-total-price]');
      this.noteEl = this.querySelector('[data-bar-note]');
      this.basePrice = Number(this.dataset.basePrice || 0);
      this.baseWeight = Number(this.dataset.baseWeight || 0);
      this.modelKey = this.dataset.modelKey;
      this.querySelectorAll('[data-group]').forEach((g) => {
        CATEGORY_LABELS[g.dataset.group] = g.dataset.category || g.querySelector('legend')?.textContent.trim();
      });

      this.readUrl();
      this.update(false);
      this.scroller = this.closest('.page-wrapper') || document.scrollingElement;
      window.addEventListener('popstate', () => { if (!this.review.hidden) this.showChoices(false); });
      const params = new URLSearchParams(location.search);
      const posted = params.get('sent') === '1' || params.get('contact_posted') === 'true';
      if (this.querySelector('[data-posted]') || (posted && this.sentHere())) {
        this.classList.add('ccw-builder--sent');
        this.form.hidden = true;
        this.review.hidden = true;
        this.sent.hidden = false;
        this.fillSent();
        this.trackLead();
      } else if (this.querySelector('[data-errored]')) {
        // A rejected post can come back without the build in the address; put the sent build back
        const record = this.sentRecord();
        const hasBuild = [...new URLSearchParams(location.search).keys()].some((k) => k !== 'sent' && k !== 'contact_posted');
        if (record && record.build && !hasBuild) {
          history.replaceState(null, '', location.pathname + record.build);
          this.readUrl();
          this.update(false);
        }
        this.showReview();
      }
      this.form.addEventListener('change', (e) => {
        if (e.target.name === 'size') {
          const err = this.form.querySelector('[data-size-error]');
          if (err) err.hidden = true;
          const sizeGroup = this.form.querySelector('[data-group="size"]');
          sizeGroup?.classList.remove('has-error');
          sizeGroup?.removeAttribute('aria-describedby');
          sizeGroup?.querySelectorAll('input').forEach((input) => input.removeAttribute('aria-invalid'));
        }
        this.update(true);
      });
      this.querySelector('[data-review-button]').addEventListener('click', () => this.showReview());
      this.querySelector('[data-back]').addEventListener('click', () => this.showChoices());
      this.querySelector('[data-copy-link]')?.addEventListener('click', (e) => this.copyLink(e.currentTarget));
      const contact = this.querySelector('#ccw-build-form');
      // Request a quote saves the build to CCW Stock Sync first, which emails Michael the quote and
      // the customer a confirmation. Shopify's contact form is the backup: it only posts when the
      // app is paused, down or slow, so Michael gets one email per build, not two. Listening on this
      // element in the capture phase runs before the contact form's own handlers (its CAPTCHA).
      this.addEventListener('submit', (event) => {
        if (event.target !== contact || this.postByForm) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.sending) return;
        this.sending = true;
        const button = this.querySelector('[data-send-button]');
        if (button) {
          button.disabled = true;
          button.textContent = 'Sending\u2026';
        }
        this.fillForm();
        this.recordSend();
        this.saveBuild(contact).then((saved) => {
          if (saved) {
            // The same page Shopify returns to after the contact form: shows the sent view and counts the lead
            location.assign(this.querySelector('[data-field-return]')?.value || `${location.pathname}?sent=1`);
            return;
          }
          this.postByForm = true;
          if (button) button.disabled = false;
          if (contact.requestSubmit) contact.requestSubmit();
          else contact.submit();
        });
      }, true);
    }

    /** Analytics and the sent record, once per send, whichever way the build goes. */
    recordSend() {
      // Named event for analytics (Shopify customer events), so build requests are told apart
      // from other contact forms: model, size and total.
      try {
        const size = this.form.querySelector('input[name="size"]:checked')?.value || '';
        window.Shopify?.analytics?.publish?.('ccw_build_request', {
          model: this.modelKey || '',
          size,
          total: Number((this.priceEl?.textContent || '').replace(/[^0-9.]/g, '')) || 0,
        });
      } catch (e) { /* analytics is optional */ }
      // Records which page sent and the build, so a shared ?sent=1 link does not show a false
      // confirmation, a reload of the real one still does, and a rejected post can restore the build
      try {
        sessionStorage.setItem(SENT_FLAG, JSON.stringify({ path: location.pathname, build: location.search }));
      } catch (e) { /* storage blocked: see sentHere() */ }
    }

    selected() {
      const out = [];
      this.querySelectorAll('[data-group]').forEach((group) => {
        const input = group.querySelector('input:checked');
        if (!input) return;
        out.push({
          group: group.dataset.group,
          label: CATEGORY_LABELS[group.dataset.group],
          value: input.value,
          name: input.dataset.name || input.value,
          price: input.dataset.price === '' || input.dataset.price == null ? null : Number(input.dataset.price),
          weight: input.dataset.weight === '' || input.dataset.weight == null ? null : Number(input.dataset.weight),
          standard: input.dataset.standard === 'true',
          asset: input.dataset.asset || '',
          swapSrc: input.dataset.swapSrc || '',
        });
      });
      // Parts with no standard and nothing chosen still appear in the review, so Michael sees the gap.
      this.querySelectorAll('[data-group]').forEach((group) => {
        if (group.querySelector('input:checked')) return;
        out.push({ group: group.dataset.group, label: CATEGORY_LABELS[group.dataset.group], value: '', name: 'Not chosen', price: 0, weight: 0, standard: false, asset: '', swapSrc: '' });
      });
      return out;
    }

    update(fromUser) {
      this.linkPowerToCrank();
      const picks = this.selected();
      let price = this.basePrice;
      let weight = this.baseWeight;
      let onQuote = 0;
      let notChosen = 0;
      picks.forEach((p) => {
        if (p.group === 'size') return;
        if (!p.value) { notChosen += 1; return; }
        if (p.price == null) onQuote += 1;
        else price += p.price;
        if (p.weight != null) weight += p.weight;
      });
      this.total = price;
      this.onQuote = onQuote;
      this.unresolved = onQuote + notChosen;
      this.priceEl.textContent = (this.unresolved ? 'From ' : '') + money(price);
      // Bar line: the chosen size and the upgrades total ("Size 54 · $1,300 of upgrades"), or a
      // prompt until a size is chosen. Weight joins once the bike data has weights.
      const parts = [];
      const size = picks.find((p) => p.group === 'size' && p.value);
      if (!size) parts.push('Choose a size to continue');
      else parts.push('Size ' + size.value);
      const upgrades = price - this.basePrice;
      if (size && upgrades > 0) parts.push(money(upgrades) + ' of upgrades');
      else if (size) parts.push('Standard spec');
      if (this.baseWeight > 0) parts.push((weight / 1000).toFixed(1) + ' kg estimated');
      if (onQuote) parts.push(onQuote + (onQuote === 1 ? ' part priced on quote' : ' parts priced on quote'));
      if (notChosen) parts.push(notChosen + (notChosen === 1 ? ' part not chosen' : ' parts not chosen'));
      this.noteEl.textContent = parts.join('  ·  ');

      const wheels = picks.find((p) => p.group === 'wheels');
      if (wheels) this.swapWheels(wheels);
      const colour = this.form.querySelector('[data-group="colour"] input:checked');
      if (colour) this.showColour(colour);
      if (fromUser) this.writeUrl(picks);
    }

    /**
     * Power meters fit the Elilee crank only (Michael, 12 Oct), so they unlock with a crank upgrade.
     * A SRAM power meter is the exception: it fits the SRAM groupset's own crank (Michael, 27 Oct),
     * so it is open while the crank from the groupset is kept.
     */
    linkPowerToCrank() {
      const crank = this.form.querySelector('[data-group="crankset"]');
      const power = this.form.querySelector('[data-group="power-meter"]');
      if (!crank || !power) return;
      const chosen = crank.querySelector('input:checked');
      const upgraded = Boolean(chosen && chosen.dataset.standard !== 'true');
      let locked = 0;
      power.querySelectorAll('input').forEach((input) => {
        if (input.dataset.standard === 'true') return;
        const fitsGroupsetCrank = (input.dataset.name || '').startsWith('SRAM');
        const open = fitsGroupsetCrank ? !upgraded : upgraded;
        input.disabled = !open;
        if (!open) locked++;
        if (!open && input.checked) {
          input.checked = false;
          const none = power.querySelector('input[data-standard="true"]');
          if (none) none.checked = true;
        }
      });
      const all = power.querySelectorAll('input:not([data-standard="true"])').length;
      power.classList.toggle('is-locked', locked > 0 && locked === all);
      const hint = power.querySelector('[data-power-hint]');
      if (hint) hint.hidden = upgraded;
    }

    /**
     * The chosen colour's name sits beside the label, and the bike photo changes to the complete
     * build in that paint. The standard colour brings the model photo back (meeting, 27 Oct).
     */
    showColour(input) {
      if (!this.bike || !this.bikePhoto) return;
      const frame = input.dataset.colourSrc || '';
      const set = (src, srcset, sizes) => {
        this.bike.style.opacity = '0';
        setTimeout(() => {
          if (srcset) this.bike.setAttribute('srcset', srcset); else this.bike.removeAttribute('srcset');
          if (sizes) this.bike.setAttribute('sizes', sizes); else this.bike.removeAttribute('sizes');
          this.bike.src = src;
          this.bike.style.opacity = '';
        }, 160);
      };
      if (this.colourLabel) this.colourLabel.textContent = input.dataset.name || '';
      if (!frame) {
        if (this.bike.getAttribute('src') !== this.bikePhoto.src) set(this.bikePhoto.src, this.bikePhoto.srcset, this.bikePhoto.sizes);
        return;
      }
      if (this.bike.getAttribute('src') === frame) return;
      const img = new Image();
      img.onload = () => set(frame, '', '');
      img.src = frame;
    }

    swapWheels(pick) {
      // The label names the wheel in the picture, so it changes only when the picture does.
      // A wheel with no swap image (or a 404) keeps the current picture and its label.
      const shown = this.querySelector('[data-shown-with]');
      if (!this.bike?.dataset.swap || !pick.swapSrc) return;
      const next = pick.swapSrc;
      if (this.bike.getAttribute('src') === next) { if (shown) shown.textContent = pick.name; return; }
      const img = new Image();
      img.onload = () => {
        this.bike.style.opacity = '0';
        setTimeout(() => {
          this.bike.src = next; this.bike.style.opacity = '';
          if (shown) shown.textContent = pick.name;
        }, 160);
      };
      img.src = next;
    }

    readUrl() {
      const params = new URLSearchParams(location.search);
      params.forEach((value, key) => {
        const input = this.form.querySelector(`input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`);
        if (input) input.checked = true;
      });
    }

    writeUrl(picks) {
      const params = new URLSearchParams();
      picks.forEach((p) => { if (p.value && (!p.standard || p.group === 'size')) params.set(p.group, p.value); });
      const qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
    }

    buildText(picks) {
      return picks.map((p) => `${p.label}: ${p.name}`).join('\n');
    }

    renderSpec(spec, picks) {
      spec.innerHTML = '';
      picks.forEach((p) => {
        const dt = document.createElement('dt');
        dt.textContent = p.label;
        const dd = document.createElement('dd');
        const name = document.createElement('span');
        name.textContent = p.name;
        const delta = document.createElement('span');
        delta.className = 'ccw-spec__delta';
        delta.textContent = p.group === 'size' || !p.value ? '' : p.standard ? 'Included' : p.price === 0 ? 'No extra' : p.price == null ? 'On quote' : (p.price > 0 ? '+' : '') + money(p.price);
        if (!p.value) dd.classList.add('ccw-spec__row--empty');
        dd.append(name, delta);
        spec.append(dt, dd);
      });
    }

    fillSent() {
      const picks = this.selected();
      this.renderSpec(this.querySelector('[data-sent-spec]'), picks);
      this.querySelector('[data-sent-total]').textContent = 'Build total ' + (this.unresolved ? 'from ' : '') + money(this.total);
      const kicker = this.querySelector('[data-sent-kicker]');
      const size = picks.find((p) => p.group === 'size' && p.value);
      if (kicker) kicker.textContent = this.dataset.modelName + (size ? ', size ' + size.value : '') + ', ' + (this.unresolved ? 'from ' : '') + money(this.total);
    }

    /** The send this tab made from this page, or null. */
    sentRecord() {
      try {
        const record = JSON.parse(sessionStorage.getItem(SENT_FLAG) || 'null');
        return record && record.path === location.pathname ? record : null;
      } catch (e) {
        return null;
      }
    }

    /** True when this tab sent the build from this page. Without storage, trust the URL rather than hide a real send. */
    sentHere() {
      try {
        sessionStorage.getItem(SENT_FLAG);
      } catch (e) {
        return true;
      }
      return Boolean(this.sentRecord());
    }

    /**
     * GA4 generate_lead for a quote request the store accepted, sent to the gtag the Google & YouTube
     * app loads. Marked as a key event in GA4 and imported to Google Ads as the build lead conversion.
     * Once per send: a reload of the confirmation does not count again.
     */
    trackLead(retry = true) {
      if (typeof window.gtag !== 'function') {
        // The app's tag can arrive after this element; try once more when the page has loaded
        if (retry) window.addEventListener('load', () => this.trackLead(false), { once: true });
        return;
      }
      const record = this.sentRecord();
      if (record && record.tracked) return;
      const size = this.form.querySelector('input[name="size"]:checked')?.value || '';
      window.gtag('event', 'generate_lead', {
        currency: 'AUD',
        value: Math.round(this.total) || 0,
        lead_source: 'build_quote',
        item_id: this.dataset.model || '',
        item_variant: size,
      });
      if (record) {
        try { sessionStorage.setItem(SENT_FLAG, JSON.stringify({ ...record, tracked: true })); } catch (e) { /* counted once per load */ }
      }
    }

    /** Starting over: the next ?sent=1 on this page must come from a new send. */
    forgetSent() {
      try { sessionStorage.removeItem(SENT_FLAG); } catch (e) { /* nothing stored */ }
    }

    scrollTop() {
      if (this.scroller && typeof this.scroller.scrollTo === 'function') this.scroller.scrollTo({ top: 0 });
      window.scrollTo({ top: 0 });
    }

    sizeChosen() {
      const group = this.form.querySelector('[data-group="size"]');
      if (!group || group.querySelector('input:checked')) return true;
      const error = group.querySelector('[data-size-error]');
      if (error) error.hidden = false;
      group.classList.add('has-error');
      group.setAttribute('aria-describedby', 'ccw-size-error');
      group.querySelectorAll('input').forEach((input) => input.setAttribute('aria-invalid', 'true'));
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      group.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      group.querySelector('input')?.focus({ preventScroll: true });
      return false;
    }

    showReview() {
      if (!this.sizeChosen()) return;
      const picks = this.selected();
      this.renderSpec(this.querySelector('[data-spec]'), picks);
      this.querySelector('[data-total]').textContent = (this.unresolved ? 'From ' : '') + money(this.total);
      this.querySelector('[data-weight-line]').textContent = this.noteEl.textContent;
      this.form.hidden = true;
      this.review.hidden = false;
      this.querySelector('[data-review-button]').hidden = true;
      this.querySelector('[data-send-button]').hidden = false;
      if (!history.state?.ccwReview) history.pushState({ ccwReview: true }, '', location.href);
      this.scrollTop();
      this.review.querySelector('h1')?.focus({ preventScroll: true });
    }

    showChoices(pop = true) {
      if (this.classList.contains('ccw-builder--sent')) this.forgetSent();
      this.review.hidden = true;
      this.form.hidden = false;
      this.querySelector('[data-review-button]').hidden = false;
      this.querySelector('[data-send-button]').hidden = true;
      if (pop && history.state?.ccwReview) history.back();
      this.scrollTop();
      this.form.querySelector('h1')?.focus({ preventScroll: true });
    }

    /**
     * Saves the build to CCW Stock Sync (Builds page) through the app proxy. True only when the app
     * saved it; false when it is paused, refuses it, fails or takes longer than 8 seconds, and then
     * the contact form posts instead. A filled spam trap never goes to the app.
     */
    async saveBuild(contact) {
      try {
        if ((contact.elements.ccw_hp?.value || '').trim()) return false;
        const field = (name) => (contact.elements[name]?.value || '').trim();
        const picks = this.selected().map((p) => ({
          group: p.group,
          label: p.label || p.group,
          name: p.name,
          price: p.price,
          standard: p.standard,
        }));
        const body = JSON.stringify({
          model: this.dataset.model || this.modelKey || '',
          modelName: this.dataset.modelName || '',
          link: this.querySelector('[data-field-link]')?.value || location.href,
          name: field('contact[name]'),
          email: field('contact[email]'),
          phone: field('contact[phone]') || null,
          note: field('contact[body]') || null,
          basePrice: this.basePrice,
          total: this.total,
          picks,
        });
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const response = await fetch('/apps/build', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!response.ok) return false;
        const saved = await response.json();
        return Boolean(saved && saved.id && !saved.paused);
      } catch (e) {
        return false;
      }
    }

    fillForm() {
      const picks = this.selected();
      this.writeUrl(picks);
      // The link goes in its own Build Link field, so the build text ends at the total
      const text = `${this.dataset.modelName}\n${this.buildText(picks)}\nTotal: ${(this.unresolved ? 'from ' : '') + money(this.total)}`;
      this.querySelector('[data-field-build]').value = text;
      this.querySelector('[data-field-link]').value = location.href;
      const params = new URLSearchParams(location.search);
      params.set('sent', '1');
      this.querySelector('[data-field-return]').value = location.pathname + '?' + params.toString();
    }

    async copyLink(button) {
      const params = new URLSearchParams(location.search);
      params.delete('sent');
      const url = location.origin + location.pathname + (params.toString() ? '?' + params : '');
      try {
        await navigator.clipboard.writeText(url);
        button.textContent = 'Link copied';
      } catch {
        button.textContent = 'Copy the link from the address bar';
      }
    }
  }

  if (!customElements.get('ccw-builder')) customElements.define('ccw-builder', CcwBuilder);
})();
